"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import {
  blockedClaimCopy,
  claimErrorMessage,
  parseRecoveryWindow,
  type BlockedClaimCopy,
} from "@/lib/draft-projects/claim-reasons";
import { ClaimBlockedPanel } from "@/components/funnel/claim-blocked-panel";

export function MergeDraft({
  sessionToken,
  title,
}: {
  sessionToken: string;
  title: string | null;
}) {
  const router = useRouter();
  const [claiming, setClaiming] = useState(false);
  const [finalisingPccp, setFinalisingPccp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<BlockedClaimCopy | null>(null);

  async function claim() {
    setClaiming(true);
    setFinalisingPccp(false);
    setError(null);
    setBlocked(null);
    try {
      for (let attempt = 0; attempt < 90; attempt += 1) {
        const res = await fetch(`/api/draft-projects/${sessionToken}/claim`, {
          method: "POST",
        });
        const data = (await res.json()) as {
          redirect?: string;
          error?: string;
          recoverUntil?: unknown;
          readOnlyReason?: unknown;
        };
        if (res.ok && data.redirect) {
          router.push(data.redirect);
          return;
        }
        if (data.error === "draft_pcpp_processing" && attempt < 89) {
          setFinalisingPccp(true);
          await new Promise((resolve) => window.setTimeout(resolve, 2500));
          continue;
        }
        // Account-state denials (plan limit, cancelled, role) are terminal
        // until the account changes, so they get the explanation + a real next
        // step instead of a retryable error. THIS is the door a signed-in
        // Starter user at their limit comes through, so it is the one that
        // matters most for issue #2103 — before the shared copy module it
        // rendered the raw `project_limit_reached` token in red.
        const blockedCopy = blockedClaimCopy(
          data.error,
          parseRecoveryWindow(data.recoverUntil),
          // past_due vs cancelled read-only copy (#2716 review F8).
          data.readOnlyReason,
        );
        if (blockedCopy) {
          setBlocked(blockedCopy);
          setClaiming(false);
          setFinalisingPccp(false);
          return;
        }
        throw new Error(claimErrorMessage(data.error));
      }
      throw new Error("The PCCP is still being checked. Please try again.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setClaiming(false);
      setFinalisingPccp(false);
    }
  }

  if (blocked) {
    // The SHARED panel — same copy, same new-tab CTA, same live retry as the
    // post-signup callback door. See its file header: these two screens have
    // now drifted apart twice, so they render one component rather than two
    // implementations kept in step by review.
    return <ClaimBlockedPanel copy={blocked} onRetry={claim} />;
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-6">
      <h1 className="font-heading text-2xl font-extrabold text-navy">Save this draft to your account</h1>
      <p className="mt-3 text-sm leading-6 text-muted">
        {finalisingPccp
          ? "The original PCCP is saved. We are finishing the PDF check before opening your project."
          : `You are signed in and have an unfinished draft${title ? ` for ${title}` : ""}. Claim it now to keep working in your project workspace.`}
      </p>
      {error ? <p className="mt-4 text-sm font-semibold text-danger">{error}</p> : null}
      <button
        type="button"
        onClick={claim}
        disabled={claiming}
        className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-5 py-3 text-sm font-bold text-white hover:bg-accent-dark disabled:opacity-50"
      >
        {finalisingPccp ? "Finalising PCCP..." : claiming ? "Claiming..." : "Claim and continue"}
      </button>
    </div>
  );
}

